import { PayloadAction } from '@reduxjs/toolkit';
import { put } from 'redux-saga/effects';

import * as API from 'services';

import {
	formatApplicantByFranchiseExtensionStayList,
	formatApplicantForExtensionStayList,
	formatApplicantListByIds,
	formatExtensionStayExcelList,
	formatExtensionStayList,
	formatWorkerExtensionStayDetail,
} from './worker-extension-stay.helper';
import { WorkerExtensionStayActions } from './worker-extension-stay.redux';
import {
	ICreateApplicantForExtensionStayRequest,
	ICreateWorkerExtensionStayRequest,
	IPayloadDeleteApplicantExtensionStayRequest,
	IPayloadDeleteExtensionStayRequest,
	IPayloadDeleteWorkerExtensionStayRequest,
	IPayloadGetApplicantByFranchiseExtensionStayListRequest,
	IPayloadGetApplicantForExtensionStayListRequest,
	IPayloadGetApplicantListByIdsRequest,
	IPayloadGetWorkerExtensionStayDetailRequest,
	IPayloadGetWorkerExtensionStayExcelListRequest,
	IPayloadGetWorkerExtensionStayListRequest,
	IUpdateFranchiseWorkerExtensionStayRequest,
} from './worker-extension-stay.type';

export function* getWorkerExtensionStayListSaga(
	action: PayloadAction<IPayloadGetWorkerExtensionStayListRequest>
) {
	const { searchTypes, callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetExtensionStayListResponse> =
			yield API.getExtensionStayList({ searchTypes, ...rest });
		const content = formatExtensionStayList(res?.data?.content);
		yield put(
			WorkerExtensionStayActions.getWorkerExtensionStayListSuccess({
				...rest,
				content,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerExtensionStayActions.getWorkerExtensionStayListFailure());
	}
}

export function* getWorkerExtensionStayExcelListSaga(
	action: PayloadAction<IPayloadGetWorkerExtensionStayExcelListRequest>
) {
	const { searchTypes, callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetExtensionStayListResponse> =
			yield API.getExtensionStayList({ searchTypes, ...rest });
		const content = formatExtensionStayExcelList(res?.data?.content);

		yield put(
			WorkerExtensionStayActions.getWorkerExtensionStayExcelListSuccess()
		);

		callback?.({ data: content });
	} catch (error) {
		callback?.({ error });
		yield put(
			WorkerExtensionStayActions.getWorkerExtensionStayExcelListFailure()
		);
	}
}

export function* getWorkerExtensionStayDetail(
	action: PayloadAction<IPayloadGetWorkerExtensionStayDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.IWorkerExtensionStay> =
			yield API.getWorkerExtensionStayDetail(id);
		const workerExtensionStayDetail = formatWorkerExtensionStayDetail(res.data);

		yield put(
			WorkerExtensionStayActions.getWorkerExtensionStayDetailSuccess({
				workerExtensionStayDetail,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerExtensionStayActions.getWorkerExtensionStayDetailFailure());
	}
}

export function* createWorkerExtensionStay(
	action: PayloadAction<ICreateWorkerExtensionStayRequest>
) {
	const { callback, data } = action.payload;

	try {
		yield API.createWorkerExtensionStay(data);

		yield put(WorkerExtensionStayActions.createWorkerExtensionStaySuccess());

		callback?.();
	} catch (error: unknown) {
		callback?.(error as API.ApiResponseError);

		yield put(WorkerExtensionStayActions.createWorkerExtensionStayFailure());
	}
}

export function* getApplicantByFranchiseExtensionStay(
	action: PayloadAction<IPayloadGetApplicantByFranchiseExtensionStayListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetApplicantByFranchiseExtensionStayListResponse> =
			yield API.getApplicantByFranchiseExtensionStay(rest);

		const content = formatApplicantByFranchiseExtensionStayList(res?.data);

		yield put(
			WorkerExtensionStayActions.getApplicantByFranchiseExtensionStayListSuccess(
				{
					...rest,
					content,
				}
			)
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerExtensionStayActions.getWorkerExtensionStayListFailure());
	}
}

export function* getApplicantListByIds(
	action: PayloadAction<IPayloadGetApplicantListByIdsRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetApplicantListByIdsResponse> =
			yield API.getApplicantListByIds(rest);

		const content = formatApplicantListByIds(res?.data);

		yield put(
			WorkerExtensionStayActions.getApplicantListByIdsSuccess({
				...rest,
				content,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerExtensionStayActions.getApplicantListByIdsFailure());
	}
}

export function* deleteWorkerExtensionStay(
	action: PayloadAction<IPayloadDeleteWorkerExtensionStayRequest>
) {
	const { ids, callback } = action.payload;

	try {
		yield API.deleteWorkerExtensionStay(ids);

		yield put(WorkerExtensionStayActions.deleteWorkerExtensionStaySuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerExtensionStayActions.deleteWorkerExtensionStayFailure());
	}
}

export function* updateFranchiseWorkerExtensionStay(
	action: PayloadAction<IUpdateFranchiseWorkerExtensionStayRequest>
) {
	const { data, callback } = action.payload;

	try {
		yield API.updateFranchiseWorkerExtensionStay(data);

		yield put(
			WorkerExtensionStayActions.updateFranchiseWorkerExtensionStaySuccess()
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerExtensionStayActions.updateFranchiseWorkerExtensionStayFailure()
		);
	}
}

export function* getApplicantForExtensionStayList(
	action: PayloadAction<IPayloadGetApplicantForExtensionStayListRequest>
) {
	const { callback, id, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.IApplicantForExtensionStay[]> =
			yield API.getApplicantForExtensionStayList(id);

		const content = formatApplicantForExtensionStayList(res?.data);
		yield put(
			WorkerExtensionStayActions.getApplicantForExtensionStayListSuccess({
				...rest,
				content,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(
			WorkerExtensionStayActions.getApplicantForExtensionStayListFailure()
		);
	}
}

export function* deleteApplicantExtensionStay(
	action: PayloadAction<IPayloadDeleteApplicantExtensionStayRequest>
) {
	const { data, callback, id } = action.payload;

	try {
		yield API.deleteApplicantExtensionStay(data, id);

		yield put(WorkerExtensionStayActions.deleteApplicantExtensionStaySuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerExtensionStayActions.deleteApplicantExtensionStayFailure());
	}
}

export function* createApplicantForExtensionStay(
	action: PayloadAction<ICreateApplicantForExtensionStayRequest>
) {
	const { callback, data, id } = action.payload;

	try {
		yield API.createApplicantForExtensionStay(data, id);

		yield put(
			WorkerExtensionStayActions.createApplicantForExtensionStaySuccess()
		);

		callback?.();
	} catch (error: unknown) {
		callback?.(error as API.ApiResponseError);

		yield put(
			WorkerExtensionStayActions.createApplicantForExtensionStayFailure()
		);
	}
}

export function* deleteExtensionStay(
	action: PayloadAction<IPayloadDeleteExtensionStayRequest>
) {
	const { id, callback } = action.payload;

	try {
		yield API.deleteExtensionStay(id);

		yield put(WorkerExtensionStayActions.deleteExtensionStaySuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerExtensionStayActions.deleteExtensionStayFailure());
	}
}
